import { useQuery } from '@tanstack/react-query';
import { Check, Smartphone, X } from 'lucide-react';
import { Link } from 'react-router';

import { PRICING_SECTION, PRICING_TEASERS, type PricingTeaser } from '@/data/homeContent';
import { fetchPublicCatalogue, type CataloguePlan } from '@/features/billing/api/billing';
import { isGranted } from '@/features/billing/lib/features';
import { PRICING_PAGE } from '@/features/marketing/content/pricingPage';
import {
  billedPhrase,
  formatPlanPrice,
  leadingInterval,
  perMonth,
  popularPlanCode,
  selfServePlans,
} from '@/features/marketing/pricing';
import { cn } from '@/lib/cn';

/**
 * The three-plan teaser, the right half of the first two-column row.
 *
 * A taste of the ladder, not the ladder itself - the feature table, the
 * billing-cycle switch and every plan live on `/pricing`, which is where the
 * popular card sends anyone who wants to compare.
 *
 * The prices are the published ones, read from the same catalogue `/pricing`
 * renders and held under the same cache key, so a visitor who opens one after
 * the other pays for a single request. They are not written into this page and
 * should never be again: the figures this section shipped with went stale
 * without anyone noticing, and a home page quoting a plan at a third of its
 * real price is worse than a home page quoting no price at all.
 *
 * Three cards, three jobs. The entry plan is tinted and carries the one filled
 * button, because it is the thing most visitors should press. The popular plan
 * is white with a green edge and the badge - the same plan `/pricing` badges,
 * derived the same way - so it stands out by outline rather than by shouting
 * over the plan beside it. The quoted plan is a plain card: no price to
 * compare, only a conversation to start. It is the platform's first sales-led
 * plan (Enterprise, "Call Sales") when one is published, and the card written
 * in `homeContent` until then - so the page never shows Enterprise twice.
 */

/** A card as it is drawn. `price` absent means there is nothing to quote yet. */
interface TeaserCard extends PricingTeaser {
  /** "₹14,999 billed yearly" - absent where the plan already bills monthly. */
  billed: string | null;
}

/**
 * The teasers with the published plans written over them.
 *
 * The first card takes the cheapest plan on the cadence the owner leads with,
 * the second the one `/pricing` badges - so the two pages push the same plan -
 * and the third takes the first sales-led plan, if there is one. A platform selling too few plans to fill the
 * middle slot leaves it as written rather than showing one plan twice.
 */
function teaserCards(plans: CataloguePlan[], preferred?: string | null): TeaserCard[] {
  const sales = plans.find((plan) => plan.contact_sales);
  const base = PRICING_TEASERS.map((teaser, index) =>
    index === 2 && sales
      ? {
          ...teaser,
          name: sales.name,
          tagline: sales.description ?? teaser.tagline,
          price: PRICING_PAGE.contactSales.price,
          period: '',
          cta: { label: PRICING_PAGE.contactSales.cta, to: PRICING_PAGE.contactSales.to },
          mobileApp: hasMobileApp(sales),
          billed: null,
        }
      : { ...teaser, billed: null },
  );
  // In the catalogue's own order, because that is the order `/pricing` badges
  // from: sorting first would hand the badge to a different plan than the one
  // wearing it two clicks away.
  const onSale = selfServePlans(plans).filter(
    (plan) => plan.billing_interval === leadingInterval(plans, preferred),
  );
  if (onSale.length === 0) return base;

  const byPrice = [...onSale].sort((a, b) => a.price_minor - b.price_minor);
  const entry = byPrice[0];
  // The plan `/pricing` badges; failing that - too short a ladder to have a
  // middle - the dearest, which is the furthest a visitor could move up.
  const popular =
    onSale.find((plan) => plan.code === popularPlanCode(onSale)) ?? byPrice[byPrice.length - 1];

  return base.map((card, index) => {
    // The middle slot is skipped when the badge lands on the plan already in
    // the first: one plan shown twice is not a ladder.
    const plan = index === 0 ? entry : index === 1 && popular !== entry ? popular : null;
    if (plan === null) return card;
    const monthly = perMonth(plan);
    return {
      ...card,
      name: plan.name,
      tagline: plan.description ?? card.tagline,
      mobileApp: hasMobileApp(plan),
      price: monthly ? monthly.price : formatPlanPrice(plan.price_minor),
      period: PRICING_SECTION.periodLabel,
      billed: monthly ? `${monthly.billed} ${billedPhrase(plan).toLowerCase()}` : null,
    };
  });
}

/** The QuickInstall app - the home page's own band above - as this plan grants it. */
function hasMobileApp(plan: CataloguePlan): boolean {
  return isGranted(plan.features.INSTALLABLE_STOREFRONT);
}

export function PricingPanel() {
  // The pricing page's own query, by key as well as by shape: whichever page a
  // visitor opens first pays for the request and the other reads the cache.
  const catalogue = useQuery({
    queryKey: ['public', 'pricing-catalogue'],
    queryFn: fetchPublicCatalogue,
    staleTime: 5 * 60 * 1000,
  });

  const plans = catalogue.data?.plans ?? [];
  const cards = catalogue.data
    ? teaserCards(plans, catalogue.data.default_billing_interval)
    : PRICING_TEASERS.map((teaser) => ({ ...teaser, billed: null }));

  // "Start free" is a promise about the plans on sale, so it is withdrawn the
  // moment the catalogue says none of them is free. With no catalogue to check
  // against, the line written on the page stands, as the names do.
  const freeToStart =
    !catalogue.data || selfServePlans(plans).some((plan) => plan.price_minor === 0);

  return (
    <section
      aria-labelledby="home-pricing"
      className="rounded-2xl border border-line/70 bg-surface/70 p-4 shadow-tile sm:p-5"
    >
      <h2 id="home-pricing" className="text-lg font-bold tracking-tight text-ink sm:text-xl">
        {PRICING_SECTION.heading}
      </h2>
      <p className="mt-1 text-xs leading-relaxed text-muted sm:text-sm">
        {freeToStart ? PRICING_SECTION.subheading : PRICING_SECTION.paidSubheading}
      </p>

      {/* `mt-5` rather than `mt-4`: the badge on the middle card sits above its
          top edge and needs the room. */}
      <ul className="mt-5 grid gap-3 sm:grid-cols-3">
        {cards.map((plan, index) => (
          <li key={plan.cta.to} className="flex">
            <div
              className={cn(
                'relative flex w-full flex-col rounded-xl border p-4',
                index === 0 && 'border-brand-200 bg-brand-50',
                plan.popular && 'border-brand-400 bg-white ring-1 ring-brand-100',
                index !== 0 && !plan.popular && 'border-line bg-white',
              )}
            >
              {plan.popular ? (
                // Centred by a full-width flex row rather than a half-width
                // translate: a translate has to be flipped for right-to-left,
                // and two competing transforms on one element is a bug waiting
                // for the first Arabic or Urdu visitor.
                <span className="pointer-events-none absolute -top-2.5 inset-x-0 flex justify-center">
                  <span className="whitespace-nowrap rounded-full bg-brand-600 px-2.5 py-0.5 text-[0.65rem] font-semibold text-white">
                    {PRICING_SECTION.popularLabel}
                  </span>
                </span>
              ) : null}

              <h3
                className={cn(
                  'mt-1 text-sm font-bold tracking-tight',
                  // Green names the plans anyone can start on the spot. The
                  // quoted plan - the only one with a written, empty period -
                  // has no price to start on, so it stays ink, and it stays
                  // ink while the others are still loading theirs.
                  plan.period === '' ? 'text-ink' : 'text-brand-600',
                )}
              >
                {plan.name}
              </h3>
              {/* Two lines' worth of space on every card, run to two or not, so
                  the three prices sit on one line across the row instead of
                  stepping down it. */}
              <p className="mt-0.5 line-clamp-2 min-h-8 text-xs leading-4 text-muted">
                {plan.tagline}
              </p>

              {/* The price, its cycle charge, and the space both keep whether
                  or not this card has either - the buttons below line up on it.
                  While the catalogue is still coming, a bar stands in: a number
                  typed into this page is how the old ones came to be wrong. */}
              <div className="mb-4 mt-3">
                <p className="flex min-h-7 flex-wrap items-baseline gap-x-1 text-lg font-bold tracking-tight tabular-nums text-ink">
                  {plan.price ? (
                    <>
                      {plan.price}
                      {plan.period ? (
                        <span className="text-xs font-medium text-muted">{plan.period}</span>
                      ) : null}
                    </>
                  ) : catalogue.isPending ? (
                    <span className="h-4 w-20 animate-pulse self-center rounded bg-line/70" />
                  ) : null}
                </p>
                <p className="mt-0.5 min-h-4 text-[0.65rem] font-medium leading-4 text-ink-soft">
                  {plan.billed}
                </p>
              </div>

              {/* The one feature every card answers: does it come with the app. */}
              <p
                className={cn(
                  'mb-3 flex items-start gap-1.5 text-[0.7rem] font-semibold leading-4',
                  plan.mobileApp ? 'text-brand-700' : 'text-muted',
                )}
              >
                <Smartphone className="mt-px size-3.5 shrink-0" aria-hidden />
                <span className="min-w-0 flex-1">
                  {plan.mobileApp ? PRICING_SECTION.mobileAppLabel : PRICING_SECTION.mobileAppMissing}
                </span>
                {plan.mobileApp ? (
                  <Check className="mt-px size-3.5 shrink-0 text-brand-600" aria-hidden />
                ) : (
                  <X className="mt-px size-3.5 shrink-0" aria-hidden />
                )}
              </p>

              <Link
                to={plan.cta.to}
                className={cn(
                  'mt-auto inline-flex h-9 items-center justify-center rounded-lg px-3 text-xs font-semibold transition-colors',
                  index === 0
                    ? 'bg-brand-500 text-white hover:bg-brand-600'
                    : 'border border-line bg-white text-ink hover:border-brand-300 hover:text-brand-700',
                )}
              >
                {plan.cta.label}
              </Link>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
